import { openPaymentPage } from "@/lib/open-checkout";
import { localeOrigin } from "@/lib/locale";
import { friendlyError } from "@/lib/friendly-errors";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useRouterState } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMembership } from "@/hooks/useMembership";
import { getConsent } from "@/lib/cookie-consent";
import {
  getMyUpgradeOptions,
  upgradeMyStripeMembership,
  startMyPaypalUpgrade,
  finaliseMyPaypalUpgrade,
} from "@/lib/membership-upgrade.functions";

type Options = Awaited<ReturnType<typeof getMyUpgradeOptions>>;
type UpgradePlanId = "membership_6month" | "membership_yearly";

const SEEN_KEY = "rt_upgrade_popup_seen_at";
const PENDING_KEY = "rt_paypal_upgrade_plan";
/** Don't re-offer for a week after the member has seen or dismissed it. */
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Pages where the nudge is redundant or intrusive. */
const SUPPRESSED_PREFIXES = [
  "/billing", // the upgrade card already lives here
  "/admin",
  "/auth",
  "/checkout",
  "/login",
  "/signup",
];

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Login nudge for monthly members: a popup offering the 6-Month ($59.99) and
 * Yearly ($89.99) upgrades. It only appears for members with an active paid
 * subscription who are still on the monthly plan, and at most once per week.
 *
 * The upgrade changes the member's existing subscription (Stripe price swap,
 * or a PayPal revision on the same subscription id) — the monthly plan stops
 * billing the moment the upgrade completes, and no second account or second
 * subscription is ever created.
 */
export function UpgradeMembershipPopup() {
  const { isMember, isAdmin, loading, isAuthed } = useMembership();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const load = useServerFn(getMyUpgradeOptions);
  const upgradeStripe = useServerFn(upgradeMyStripeMembership);
  const startPaypal = useServerFn(startMyPaypalUpgrade);
  const finalisePaypal = useServerFn(finaliseMyPaypalUpgrade);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Options | null>(null);
  const [pendingPlan, setPendingPlan] = useState<UpgradePlanId | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<UpgradePlanId | null>(null);
  const decidedRef = useRef(false);

  // Check eligibility once the membership state resolves (i.e. right after
  // signing in or when a restored session is confirmed).
  useEffect(() => {
    if (loading || !isAuthed || !isMember || isAdmin || decidedRef.current) return;
    if (SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p))) return;
    decidedRef.current = true;

    load({ data: undefined as never })
      .then((res) => {
        // Only nudge members still on the monthly plan: both upgrade options
        // are available (6-Month and Yearly). Already-upgraded members are
        // left alone — the billing page card covers their next step.
        const eligible =
          res.status === "active" && res.provider !== "none" && res.options.length >= 2;
        if (!eligible) return;
        const seen = Number(window.localStorage.getItem(SEEN_KEY) ?? 0);
        if (Date.now() - seen < COOLDOWN_MS) return;
        // Never pop up while the cookie banner is still showing — a tap meant
        // for "Accept" must not land on an upgrade button.
        if (!getConsent()) return;
        setData(res);
        window.localStorage.setItem(SEEN_KEY, String(Date.now()));
        setOpen(true);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthed, isMember, isAdmin, pathname]);

  // Returning from PayPal approval: confirm the revision and mirror it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") !== "paypal") return;
    const planId = window.sessionStorage.getItem(PENDING_KEY);
    if (!planId) return;
    window.sessionStorage.removeItem(PENDING_KEY);
    finalisePaypal({ data: { plan_id: planId as UpgradePlanId } })
      .then((r) => {
        if (r.ok) {
          toast.success("Your membership has been upgraded", {
            description: r.next_billing
              ? `Your next renewal is ${formatDate(r.next_billing)}. Your monthly plan has stopped.`
              : "Your monthly plan has stopped.",
            duration: 9000,
          });
          setOpen(false);
        } else {
          toast.message("Upgrade is being confirmed", { description: r.message, duration: 9000 });
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch {
      /* storage unavailable — nothing to remember */
    }
    setOpen(false);
  };

  async function choose(planId: UpgradePlanId) {
    setPendingPlan(planId);
    try {
      if (data!.provider === "stripe") {
        const r = await upgradeStripe({ data: { plan_id: planId } });
        if (r.ok) {
          toast.success("Your membership has been upgraded", {
            description: r.current_period_end
              ? `Your monthly plan has stopped. Your next renewal is ${formatDate(r.current_period_end)}.`
              : "Your monthly plan has stopped.",
            duration: 9000,
          });
          setOpen(false);
        } else {
          toast.error("Upgrade not completed", { description: r.message, duration: 9000 });
        }
        return;
      }

      const origin = localeOrigin();
      const r = await startPaypal({
        data: {
          plan_id: planId,
          return_url: `${origin}/billing?upgrade=paypal`,
          cancel_url: `${origin}/billing?upgrade=cancelled`,
        },
      });
      if (!r.ok) {
        toast.error("Upgrade not completed", { description: r.message, duration: 9000 });
        return;
      }
      if (r.approve_url) {
        window.sessionStorage.setItem(PENDING_KEY, planId);
        openPaymentPage(r.approve_url);
        return;
      }
      const done = await finalisePaypal({ data: { plan_id: planId } });
      toast[done.ok ? "success" : "message"](
        done.ok ? "Your membership has been upgraded" : "Upgrade is being confirmed",
        { description: done.ok ? "Your monthly plan has stopped." : done.message, duration: 9000 },
      );
      if (done.ok) setOpen(false);
    } catch (err) {
      toast.error("Upgrade not completed", {
        description: friendlyError(err),
      });
    } finally {
      setPendingPlan(null);
    }
  }

  if (!open || !data) return null;

  const nextRenewal = formatDate(data.next_renewal);
  const confirmOpt = confirmPlan ? data.options.find((o) => o.id === confirmPlan) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-lg border-gold/40 bg-card p-0">
        <div className="relative max-h-[85vh] overflow-y-auto p-6 sm:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-16 end-0 h-40 w-40 rounded-full bg-[var(--gold-deep)]/10 blur-3xl"
          />
          <DialogHeader className="text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
              <Sparkles className="h-6 w-6 text-foreground" strokeWidth={1.5} />
            </div>
            <DialogTitle className="font-serif text-2xl leading-tight text-foreground sm:text-3xl">
              Upgrade your membership
            </DialogTitle>
            <DialogDescription className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground sm:text-base">
              Your current plan:{" "}
              <span className="font-medium text-foreground">{data.current_plan_label}</span>
              {nextRenewal ? (
                <>
                  {" "}
                  · Next renewal: <span className="font-medium text-foreground">{nextRenewal}</span>
                </>
              ) : null}
            </DialogDescription>
            <p className="mx-auto mt-3 max-w-sm text-xs text-muted-foreground">
              Upgrading changes your existing{" "}
              {data.provider === "stripe" ? "card" : "PayPal"} subscription — your monthly plan
              stops billing straight away, and everything in your account stays exactly as it is.
            </p>
          </DialogHeader>

          {confirmOpt ? (
            <div className="mt-6 rounded-2xl border border-gold/40 bg-card p-5 text-center">
              <p className="text-sm font-medium text-foreground">Please confirm your upgrade</p>
              <p className="mt-2 text-sm text-muted-foreground">
                You'll be charged{" "}
                <span className="font-medium text-foreground">{money(confirmOpt.price_cents)}</span>{" "}
                today for the {confirmOpt.name}, and your monthly plan will stop.
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
                <button
                  onClick={() => setConfirmPlan(null)}
                  disabled={!!pendingPlan}
                  className="rounded-full border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground"
                >
                  No, go back
                </button>
                <button
                  onClick={() => choose(confirmOpt.id as UpgradePlanId)}
                  disabled={!!pendingPlan}
                  className="btn-gold inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                >
                  {pendingPlan ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Yes, charge {money(confirmOpt.price_cents)}
                </button>
              </div>
            </div>
          ) : (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {data.options.map((opt) => (
              <div
                key={opt.id}
                className="flex flex-col rounded-2xl border border-border bg-card p-4 sm:p-5"
              >
                <div className="text-sm font-medium text-foreground">{opt.name}</div>
                <div className="mt-1 font-serif text-2xl text-foreground sm:text-3xl">
                  {money(opt.price_cents)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{opt.billing_note}</p>
                {opt.saving_note ? (
                  <p className="mt-1 text-xs text-[var(--gold-deep)]">{opt.saving_note}</p>
                ) : null}
                <button
                  onClick={() => setConfirmPlan(opt.id as UpgradePlanId)}
                  disabled={!!pendingPlan}
                  className="btn-gold mt-4 inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                >
                  {pendingPlan === opt.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Upgrade to {opt.id === "membership_yearly" ? "Yearly" : "6 Months"}
                  {!pendingPlan ? <ArrowUpRight className="h-4 w-4" /> : null}
                </button>
              </div>
            ))}
          </div>
          )}

          <button
            onClick={dismiss}
            className="mx-auto mt-5 block text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}